import { assertValidApplicationDocument } from "../domain/applicationDocument.js";

const settingsDatabaseName = "regulator-settings";
const settingsStoreName = "settings";
const settingsDatabaseVersion = 1;
const handleKey = "backup-directory";
const intervalKey = "backup-interval";
const lastBackupKey = "last-backup-at";
const intervals = { "15m": 15 * 60 * 1000, "1h": 60 * 60 * 1000, "4h": 4 * 60 * 60 * 1000, "1d": 24 * 60 * 60 * 1000 };
let databasePromise;
let backupTimer;

function database() {
  databasePromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(settingsDatabaseName, settingsDatabaseVersion);
    request.onupgradeneeded = () => request.result.objectStoreNames.contains(settingsStoreName) || request.result.createObjectStore(settingsStoreName);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return databasePromise;
}

async function read(key) {
  const db = await database();
  return requestResult(db.transaction(settingsStoreName).objectStore(settingsStoreName).get(key));
}

async function write(key, value) {
  const db = await database();
  const transaction = db.transaction(settingsStoreName, "readwrite");
  transaction.objectStore(settingsStoreName).put(value, key);
  await transactionComplete(transaction);
}

async function remove(key) {
  const db = await database();
  const transaction = db.transaction(settingsStoreName, "readwrite");
  transaction.objectStore(settingsStoreName).delete(key);
  await transactionComplete(transaction);
}

export function backupFileName(date = new Date()) {
  return `regulator-backup-${date.toISOString().replace(/[:.]/g, "-")}.json`;
}

export function createBackupPayload(applicationDocument, reason = "manual") {
  assertValidApplicationDocument(applicationDocument);
  return { format: "regulator-backup", version: 1, createdAt: new Date().toISOString(), reason, applicationDocument: structuredClone(applicationDocument) };
}

export function parseBackupPayload(value) {
  if (value?.format !== "regulator-backup" || value?.version !== 1) throw new Error("This is not a supported Regulator backup.");
  assertValidApplicationDocument(value.applicationDocument);
  return structuredClone(value.applicationDocument);
}

export async function getBackupSettings() {
  const [handle, interval, lastBackupAt] = await Promise.all([read(handleKey), read(intervalKey), read(lastBackupKey)]);
  return { handle: handle ?? null, interval: Object.hasOwn(intervals, interval) ? interval : "1h", lastBackupAt: lastBackupAt ?? null };
}

export async function chooseBackupDirectory() {
  if (typeof window.showDirectoryPicker !== "function") throw new Error("Folder backups are not supported by this browser.");
  const handle = await window.showDirectoryPicker({ id: "regulator-backup", startIn: "downloads" });
  await write(handleKey, handle);
  return handle;
}

export async function chooseRestoreBackupFile(startIn = "downloads", picker = window.showOpenFilePicker) {
  if (typeof picker !== "function") return null;
  const [handle] = await picker({
    id: "regulator-restore-backup",
    startIn,
    multiple: false,
    types: [{ description: "Regulator JSON backups", accept: { "application/json": [".json"] } }]
  });
  return handle.getFile();
}

export async function clearBackupDirectory() {
  await remove(handleKey);
  clearTimeout(backupTimer);
}

export async function setBackupInterval(interval) {
  if (!Object.hasOwn(intervals, interval)) throw new Error("Unsupported backup interval.");
  await write(intervalKey, interval);
}

export async function writeBackup(applicationDocument, reason = "manual", { requestPermission = true } = {}) {
  const handle = await read(handleKey);
  if (!handle) return { status: "missing" };
  let permission = typeof handle.queryPermission === "function" ? await handle.queryPermission({ mode: "readwrite" }) : "granted";
  if (permission !== "granted" && requestPermission && typeof handle.requestPermission === "function") permission = await handle.requestPermission({ mode: "readwrite" });
  if (permission !== "granted") return { status: "denied", targetName: handle.name ?? "" };
  const fileName = backupFileName();
  const fileHandle = await handle.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(`${JSON.stringify(createBackupPayload(applicationDocument, reason), null, 2)}\n`);
  await writable.close();
  const savedAt = new Date().toISOString();
  await write(lastBackupKey, savedAt);
  return { status: "saved", fileName, targetName: handle.name ?? "", savedAt };
}

export function scheduleAutomaticBackup(applicationDocument) {
  clearTimeout(backupTimer);
  backupTimer = setTimeout(async () => {
    try {
      const { handle, interval, lastBackupAt } = await getBackupSettings();
      if (!handle) return;
      const elapsed = Date.now() - new Date(lastBackupAt ?? 0).getTime();
      const remaining = intervals[interval] - elapsed;
      if (remaining > 0) {
        backupTimer = setTimeout(() => writeBackup(applicationDocument, "automatic", { requestPermission: false }), remaining);
        return;
      }
      await writeBackup(applicationDocument, "automatic", { requestPermission: false });
    } catch (error) {
      console.error("Automatic backup failed.", error);
    }
  }, 15_000);
}

function requestResult(request) { return new Promise((resolve, reject) => { request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); }); }
function transactionComplete(transaction) { return new Promise((resolve, reject) => { transaction.oncomplete = () => resolve(); transaction.onerror = () => reject(transaction.error); transaction.onabort = () => reject(transaction.error); }); }
